export const PROGRESS_VERSION = 1;
export const PROGRESS_KEY = 'bolt-forge.progress.v1';

function safeParse(value) {
  if (!value) return null;
  try { return JSON.parse(value); } catch { return null; }
}

export function defaultProgress() {
  return {
    version: PROGRESS_VERSION,
    unlockedLevel: 1,
    completed: {},
  };
}

export function calculateStars(level, movesUsed) {
  const limit = Math.max(1, Number(level.moveLimit) || 1);
  const thresholds = Array.isArray(level.starThresholds) && level.starThresholds.length === 3
    ? level.starThresholds.map((value) => Math.max(1, Number(value) || limit))
    : [Math.ceil(limit * 0.55), Math.ceil(limit * 0.78), limit];

  if (movesUsed <= thresholds[0]) return 3;
  if (movesUsed <= thresholds[1]) return 2;
  return 1;
}

export function createProgressStore(storage = globalThis?.localStorage) {
  let memory = defaultProgress();

  function read() {
    const raw = storage?.getItem?.(PROGRESS_KEY);
    const parsed = safeParse(raw);
    if (!parsed || parsed.version !== PROGRESS_VERSION) return { ...memory, completed: { ...memory.completed } };
    const completed = parsed.completed && typeof parsed.completed === 'object' ? { ...parsed.completed } : {};
    let unlockedLevel = Math.max(1, Number(parsed.unlockedLevel) || 1);

    // Milestone 5 campaign expansion migration: a player who had already
    // completed the old final Level 10 should enter the new Journeyman tier
    // without replaying Level 10 solely because the previous catalog ended there.
    if (completed['level-010']?.completed && unlockedLevel < 11) unlockedLevel = 11;

    memory = {
      version: PROGRESS_VERSION,
      unlockedLevel,
      completed,
    };
    return { ...memory, completed: { ...memory.completed } };
  }

  function write(next) {
    memory = {
      version: PROGRESS_VERSION,
      unlockedLevel: Math.max(1, Number(next.unlockedLevel) || 1),
      completed: { ...(next.completed || {}) },
    };
    try { storage?.setItem?.(PROGRESS_KEY, JSON.stringify(memory)); } catch { /* storage can be unavailable */ }
    return read();
  }

  function completeLevel(level, sessionState, nextDisplayNumber = null) {
    const progress = read();
    const movesUsed = Math.max(0, (Number(level.moveLimit) || 0) - Number(sessionState.movesRemaining || 0));
    const stars = calculateStars(level, movesUsed);
    const previous = progress.completed[level.id];
    const record = {
      stars: Math.max(stars, Number(previous?.stars) || 0),
      bestMovesUsed: previous?.bestMovesUsed == null ? movesUsed : Math.min(previous.bestMovesUsed, movesUsed),
      completed: true,
    };

    const unlock = nextDisplayNumber === null
      ? Number(level.displayNumber)
      : (Number(nextDisplayNumber) || (Number(level.displayNumber) + 1));
    return write({
      ...progress,
      unlockedLevel: Math.max(progress.unlockedLevel, unlock),
      completed: { ...progress.completed, [level.id]: record },
    });
  }

  function reset() {
    return write(defaultProgress());
  }

  return Object.freeze({ read, write, completeLevel, reset });
}
